/* eslint-disable import/no-duplicates */
import { useCallback, useRef } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import {
	getReactionsQueryInfo,
	getReactionsSummary,
} from '@confluence/reactions/entry-points/reactionQueryHelpers.ts';
import { getBooleanLocationTypes } from '@confluence/reactions/entry-points/reactionLocationUtils.ts';
import type {
	ReactionLocation,
	Reaction,
	GQLContentType,
	GQLContainerType,
} from '@confluence/reactions/entry-points/reactionTypes.ts';
import { ReactionContainerType } from '@confluence/reactions/entry-points/reactionTypes.ts';
import { getLogger } from '@confluence/logger';
import { getExperienceTracker } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import {
	OMNIBAR_ADD_REACTION_EXPERIENCE,
	OMNIBAR_DELETE_REACTION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

import type { useReactionMutationsAddReactionMutation$data } from './__generated__/useReactionMutationsAddReactionMutation.graphql.ts';
import type { useReactionMutationsAddReactionMutation$variables } from './__generated__/useReactionMutationsAddReactionMutation.graphql.ts';
import type { useReactionMutationsDeleteReactionMutation$data } from './__generated__/useReactionMutationsDeleteReactionMutation.graphql.ts';
import type { useReactionMutationsDeleteReactionMutation$variables } from './__generated__/useReactionMutationsDeleteReactionMutation.graphql.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

const logger = getLogger('omnibar-reactions');

const useReactionMutationsAddReactionMutation = graphql`
	mutation useReactionMutationsAddReactionMutation(
		$cloudId: ID!
		$contentId: Long!
		$containerId: Long!
		$containerType: ContainerType!
		$contentType: GraphQLReactionContentType!
		$emojiId: String!
	) {
		confluence_addReaction(
			cloudId: $cloudId
			input: {
				contentId: $contentId
				containerId: $containerId
				containerType: $containerType
				contentType: $contentType
				emojiId: $emojiId
			}
		) {
			ari
			containerAri
			emojiId
		}
	}
`;

const useReactionMutationsDeleteReactionMutation = graphql`
	mutation useReactionMutationsDeleteReactionMutation(
		$cloudId: ID!
		$contentId: Long!
		$containerId: Long!
		$containerType: ContainerType!
		$contentType: GraphQLReactionContentType!
		$emojiId: String!
	) {
		confluence_deleteReaction(
			cloudId: $cloudId
			input: {
				contentId: $contentId
				containerId: $containerId
				containerType: $containerType
				contentType: $contentType
				emojiId: $emojiId
			}
		) {
			ari
			containerAri
			emojiId
		}
	}
`;

/** Optimistic response for add reaction (matches useReactionMutationsAddReactionMutation$data). */
const createAddReactionOptimisticResponse = (emojiId: string) => ({
	__typename: 'Mutation' as const,
	confluence_addReaction: {
		__typename: 'ConfluenceReactionPayload' as const,
		ari: null,
		containerAri: null,
		emojiId,
	},
});

/** Optimistic response for delete reaction (matches useReactionMutationsDeleteReactionMutation$data). */
const createDeleteReactionOptimisticResponse = (emojiId: string) => ({
	__typename: 'Mutation' as const,
	confluence_deleteReaction: {
		__typename: 'ConfluenceReactionPayload' as const,
		ari: null,
		containerAri: null,
		emojiId,
	},
});

/**
 * Gets updated reactions response for cache update.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getUpdatedReactionsResponse = (
	data: any,
	emojiId: string,
	actionType: 'add' | 'delete',
	isComment: boolean,
) => {
	const summary = getReactionsSummary(data);
	if (!summary) return;

	let updatedCount = summary.reactionsCount ?? 0;
	const updatedReactions = [...(summary.reactionsSummaryForEmoji ?? [])];
	const reactionObject = updatedReactions.find((item) => item?.emojiId === emojiId);

	if (actionType === 'add') {
		if (reactionObject && !reactionObject.reacted) {
			const idx = updatedReactions.indexOf(reactionObject);
			updatedReactions[idx] = {
				...reactionObject,
				count: (reactionObject.count ?? 0) + 1,
				reacted: true,
			};
		} else {
			updatedReactions.push({
				emojiId,
				count: 1,
				reacted: true,
				id: `${summary.containerAri}|${summary.ari}|${emojiId}`,
				__typename: 'ReactionsSummaryForEmoji',
			} as (typeof updatedReactions)[number]);
		}
		updatedCount++;
	}

	if (actionType === 'delete') {
		if (reactionObject && reactionObject.reacted && (reactionObject.count ?? 0) > 1) {
			const idx = updatedReactions.indexOf(reactionObject);
			updatedReactions[idx] = {
				...reactionObject,
				count: (reactionObject.count ?? 0) - 1,
				reacted: false,
			};
		} else {
			const removeIdx = updatedReactions.map((item) => item?.emojiId).indexOf(emojiId);
			updatedReactions.splice(removeIdx, 1);
		}
		updatedCount--;
	}

	const getContentResponseData = () => ({
		...data,
		content: {
			...data.content,
			nodes: [
				{
					...data.content.nodes[0],
					contentReactionsSummary: {
						...summary,
						reactionsSummaryForEmoji: updatedReactions,
						reactionsCount: updatedCount,
					},
				},
			],
		},
	});

	return isComment
		? {
				...data,
				comments: {
					...data.comments,
					nodes: [
						{
							...data.comments.nodes[0],
							reactionsSummary: {
								...summary,
								reactionsSummaryForEmoji: updatedReactions,
								reactionsCount: updatedCount,
							},
						},
					],
				},
			}
		: getContentResponseData();
};

interface UseReactionMutationsProps {
	contentId: string;
	spaceId: string;
	contentType: string;
	contentSubType?: string;
	cloudId: string | null;
	edition?: string;
	reactionsMap: Map<string, Reaction>;
}

/**
 * Handles reaction add/delete mutations with Apollo cache updates.
 */
export const useReactionMutations = ({
	contentId,
	spaceId,
	contentType,
	contentSubType,
	cloudId,
	edition,
	reactionsMap,
}: UseReactionMutationsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const activeReactionsRef = useRef<Set<string>>(new Set());

	const { isComment } = getBooleanLocationTypes(contentType as ReactionLocation);

	const [addReaction] = useMutationInterop<
		useReactionMutationsAddReactionMutation$data,
		useReactionMutationsAddReactionMutation$variables
	>(useReactionMutationsAddReactionMutation);
	const [deleteReaction] = useMutationInterop<
		useReactionMutationsDeleteReactionMutation$data,
		useReactionMutationsDeleteReactionMutation$variables
	>(useReactionMutationsDeleteReactionMutation);

	const updateApolloCache = useCallback(
		(emojiId: string, actionType: 'add' | 'delete') =>
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(_cache: any, result: any) => {
				if (!result) return;

				const { query, queryVariables } = getReactionsQueryInfo(isComment, contentId, spaceId);

				try {
					const response = readQueryInterop({ query, variables: queryVariables });
					const updatedResponse = getUpdatedReactionsResponse(
						response,
						emojiId,
						actionType,
						isComment,
					);
					if (updatedResponse) {
						writeQueryInterop({ query, variables: queryVariables, data: updatedResponse });
					}
				} catch (err) {
					logger.error`An error occurred when updating cache for omnibar reaction - ${err}`;
				}
			},
		[contentId, spaceId, isComment],
	);

	const handleDefaultReactionClick = useCallback(
		(emojiId: string) => {
			if (activeReactionsRef.current.has(emojiId)) return;

			const existingReaction = reactionsMap.get(emojiId);
			if (existingReaction?.reacted) return;

			if (!cloudId || !contentId || !spaceId || !contentType) return;

			activeReactionsRef.current.add(emojiId);

			const experienceTracker = getExperienceTracker();
			experienceTracker.start({
				name: OMNIBAR_ADD_REACTION_EXPERIENCE,
				attributes: { contentId, contentType, emojiId, placement: 'omni-bar' },
			});

			void addReaction({
				variables: {
					cloudId: cloudId || '',
					contentId: Number(contentId),
					contentType: contentType?.toUpperCase() as GQLContentType,
					containerId: Number(spaceId),
					containerType: ReactionContainerType.SPACE.toUpperCase() as GQLContainerType,
					emojiId,
				},
				update: updateApolloCache(emojiId, 'add'),
				optimisticResponse: createAddReactionOptimisticResponse(emojiId),
			})
				.then(() => {
					activeReactionsRef.current.delete(emojiId);
					experienceTracker.succeed({
						name: OMNIBAR_ADD_REACTION_EXPERIENCE,
					});
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'created',
							actionSubject: 'reaction',
							source: contentType,
							attributes: {
								objectId: contentId,
								objectType: contentType,
								containerId: spaceId,
								containerType: ReactionContainerType.SPACE,
								emojiId,
								displayType: contentType as ReactionLocation,
								isLivePage: contentSubType === 'live',
								reactionsPlacement: 'omni-bar',
								edition,
							},
						},
					}).fire();
				})
				.catch((error) => {
					activeReactionsRef.current.delete(emojiId);
					logger.error`Failed to add reaction in omnibar: ${error}`;
					experienceTracker.stopOnError({
						name: OMNIBAR_ADD_REACTION_EXPERIENCE,
						error: error instanceof Error ? error : new Error(String(error)),
					});
				});
		},
		[
			reactionsMap,
			addReaction,
			cloudId,
			contentId,
			contentType,
			spaceId,
			updateApolloCache,
			createAnalyticsEvent,
			contentSubType,
			edition,
		],
	);

	const handleSummaryReactionClick = useCallback(
		(emojiId: string) => {
			if (activeReactionsRef.current.has(emojiId)) return;

			if (!cloudId || !contentId || !spaceId || !contentType) return;

			const existingReaction = reactionsMap.get(emojiId);
			const experienceTracker = getExperienceTracker();

			if (!existingReaction?.reacted) {
				activeReactionsRef.current.add(emojiId);
				experienceTracker.start({
					name: OMNIBAR_ADD_REACTION_EXPERIENCE,
					attributes: { contentId, contentType, emojiId, placement: 'omni-bar-summary' },
				});
				void addReaction({
					variables: {
						cloudId: cloudId || '',
						contentId: Number(contentId),
						contentType: contentType?.toUpperCase() as GQLContentType,
						containerId: Number(spaceId),
						containerType: ReactionContainerType.SPACE.toUpperCase() as GQLContainerType,
						emojiId,
					},
					update: updateApolloCache(emojiId, 'add'),
					optimisticResponse: createAddReactionOptimisticResponse(emojiId),
				})
					.then(() => {
						activeReactionsRef.current.delete(emojiId);
						experienceTracker.succeed({ name: OMNIBAR_ADD_REACTION_EXPERIENCE });
					})
					.catch((error) => {
						activeReactionsRef.current.delete(emojiId);
						logger.error`Failed to add reaction from summary in omnibar: ${error}`;
						experienceTracker.stopOnError({
							name: OMNIBAR_ADD_REACTION_EXPERIENCE,
							error: error instanceof Error ? error : new Error(String(error)),
						});
					});
				return;
			}

			activeReactionsRef.current.add(emojiId);
			experienceTracker.start({
				name: OMNIBAR_DELETE_REACTION_EXPERIENCE,
				attributes: { contentId, contentType, emojiId, placement: 'omni-bar-summary' },
			});

			void deleteReaction({
				variables: {
					cloudId: cloudId || '',
					contentId: Number(contentId),
					contentType: contentType?.toUpperCase() as GQLContentType,
					containerId: Number(spaceId),
					containerType: ReactionContainerType.SPACE.toUpperCase() as GQLContainerType,
					emojiId,
				},
				update: updateApolloCache(emojiId, 'delete'),
				optimisticResponse: createDeleteReactionOptimisticResponse(emojiId),
			})
				.then(() => {
					activeReactionsRef.current.delete(emojiId);
					experienceTracker.succeed({ name: OMNIBAR_DELETE_REACTION_EXPERIENCE });
				})
				.catch((error) => {
					activeReactionsRef.current.delete(emojiId);
					logger.error`Failed to delete reaction in omnibar: ${error}`;
					experienceTracker.stopOnError({
						name: OMNIBAR_DELETE_REACTION_EXPERIENCE,
						error: error instanceof Error ? error : new Error(String(error)),
					});
				});
		},
		[
			addReaction,
			deleteReaction,
			cloudId,
			contentId,
			contentType,
			spaceId,
			reactionsMap,
			updateApolloCache,
		],
	);

	return {
		handleDefaultReactionClick,
		handleSummaryReactionClick,
	};
};
